import React from 'react'
import { addListener, removeListener } from 'component-library/__helpers/global-dom-events'
import { useMounted } from 'common/hooks/useMounted'
import { IsHydrating } from 'common/context-providers/is-hydrating'
import { getShouldLoadProps } from './getShouldLoadProps'

export interface WithLazyLoadingProps {
    lazyLoadHasTriggered: boolean
    wasLazyLoaded: boolean
    innerRef: React.RefObject<HTMLElement>
}

export interface ShouldLoadProps {
    percentVisible: number // 0-100
    distanceFromViewport: number
    viewportHeight: number
}

export interface State {
    loaded: boolean
    /**
     * Captures if the loaded state was delayed past mounting
     *
     * Useful to apply loaded animations only when lazy loaded
     */
    wasLazyLoaded: boolean
}

export interface WithInnerRef {
    innerRef?: React.RefObject<HTMLElement>
}

/**
 * This fn is deprecated in favour of the hook below. Please opt to use that instead. Once all components using this HOC are refactored,
 * this can be removed.
 **/
export function withLazyLoading<P>(
    WrappedComponent:
        | React.ComponentClass<P & WithLazyLoadingProps>
        | React.FC<P & WithLazyLoadingProps>,
    shouldLoad: (shouldLoadProps: ShouldLoadProps) => boolean
): React.ComponentClass<P & WithInnerRef> {
    return class ComponentWithLazyLoading extends React.Component<P & WithInnerRef, State> {
        mounted = false
        innerRef = React.createRef<HTMLElement>()
        displayName = `ComponentWithLazyLoading(${WrappedComponent.displayName ||
            (WrappedComponent as any).name ||
            'Component'})`

        constructor(props: P & WithInnerRef) {
            super(props)

            this.state = { loaded: false, wasLazyLoaded: false }
        }

        componentDidMount() {
            this.mounted = true

            if (!this.innerRef.current) {
                console.debug(
                    `innerRef passed by 'withLazyLoading' has not been added to an element`,
                    WrappedComponent.displayName
                )
                return
            }

            const loaded = shouldLoad(getShouldLoadProps(this.innerRef.current))
            this.setState({
                loaded: loaded,
                // Component was lazy loaded if not initially loaded
                wasLazyLoaded: !loaded,
            })
            addListener('scroll', this.detectInViewport)
            addListener('resize', this.detectInViewport)
        }

        componentWillUnmount() {
            this.mounted = false
            removeListener('scroll', this.detectInViewport)
            removeListener('resize', this.detectInViewport)
        }

        // This code can likely all be removed once all browsers support IntersectionObserver
        // https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API#Browser_compatibility
        // https://bitbucket.swmdigital.io/projects/PROD/repos/web/pull-requests/431/overview
        detectInViewport = () => {
            if (!this.innerRef.current || !this.innerRef.current.getBoundingClientRect) {
                return
            }

            if (this.state.loaded) {
                // Once loaded, we stay loaded
                return
            }

            if (shouldLoad(getShouldLoadProps(this.innerRef.current))) {
                // Schedule state change for after all components have determined if they are on screen
                // See the comment about getBoundingClientRect()
                setTimeout(() => {
                    // Only update state if we haven't been unmounted while waiting for the timeout
                    if (this.mounted) {
                        this.setState({ loaded: true })
                    }
                })
            }
        }

        render() {
            return (
                <IsHydrating.Consumer>
                    {hydrating => (
                        <WrappedComponent
                            {...this.props}
                            // We don't ever want to render lazy loaded
                            // content on the initial render pass
                            lazyLoadHasTriggered={!hydrating && this.state.loaded}
                            wasLazyLoaded={this.state.wasLazyLoaded}
                            innerRef={this.innerRef}
                        />
                    )}
                </IsHydrating.Consumer>
            )
        }
    }
}

export interface LazyLoadingResult {
    lazyLoadHasTriggered: boolean
    wasLazyLoaded: boolean
    lazyLoadRef: React.RefObject<HTMLElement>
}

export function useLazyLoading(shouldLoad: (props: ShouldLoadProps) => boolean): LazyLoadingResult {
    const isHydrating = React.useContext(IsHydrating)
    const [isLoaded, setLoaded] = React.useState(false)
    const [wasLazyLoaded, setWasLazyLoaded] = React.useState(false)
    const isMounted = useMounted()
    const lazyLoadRef = React.useRef<HTMLElement | null>(null)

    React.useEffect(() => {
        if (!lazyLoadRef.current) {
            console.debug(`innerRef passed by 'useLazyLoading' has not been added to an element`)
            return
        }

        const loaded = shouldLoad(getShouldLoadProps(lazyLoadRef.current))

        setLoaded(loaded)
        // Component was lazy loaded if not initially loaded
        setWasLazyLoaded(!loaded)

        function detectInViewport() {
            if (!lazyLoadRef.current || !lazyLoadRef.current.getBoundingClientRect) return
            // Once loaded, we stay loaded
            if (loaded) return
            if (shouldLoad(getShouldLoadProps(lazyLoadRef.current))) {
                // Schedule state change for after all components have determined if they are on screen
                // See the comment about getBoundingClientRect()
                setTimeout(() => {
                    // Only update state if we haven't been unmounted while waiting for the timeout
                    if (isMounted()) {
                        setLoaded(true)
                    }
                })
            }
        }

        addListener('scroll', detectInViewport)
        addListener('resize', detectInViewport)

        return () => {
            removeListener('scroll', detectInViewport)
            removeListener('resize', detectInViewport)
        }
    }, [])

    return {
        lazyLoadHasTriggered: !isHydrating && isLoaded,
        wasLazyLoaded,
        lazyLoadRef,
    }
}
